import React from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { MAX_MISTAKES } from "../../lib/constants";
import { PuzzleDataContext } from "../PuzzleDataProvider";
import {
  loadGameStateFromLocalStorage,
  loadStatsFromLocalStorage,
  markPuzzleDayCompleted,
  saveGameStateToLocalStorage,
  saveStatsToLocalStorage,
} from "../../lib/local-storage";
import {
  isGameDataEquivalent,
  isGuessesFromGame,
} from "../../lib/game-helpers";
import { isTodayPuzzleDate } from "../../lib/time-utils";
export const GameStatusContext = React.createContext();

const createEmptyStats = () => ({
  played: 0,
  wins: 0,
  totalMistakes: 0,
  currentStreak: 0,
  maxStreak: 0,
  lastPlayedDate: null,
  playedPuzzleKeys: {},
  finalizedPuzzleKeys: {},
});

const normalizeStats = (stats) => {
  const baseStats =
    stats && typeof stats === "object"
      ? { ...createEmptyStats(), ...stats }
      : createEmptyStats();

  return {
    ...baseStats,
    playedPuzzleKeys:
      baseStats.playedPuzzleKeys &&
      typeof baseStats.playedPuzzleKeys === "object" &&
      !Array.isArray(baseStats.playedPuzzleKeys)
        ? baseStats.playedPuzzleKeys
        : {},
    finalizedPuzzleKeys:
      baseStats.finalizedPuzzleKeys &&
      typeof baseStats.finalizedPuzzleKeys === "object" &&
      !Array.isArray(baseStats.finalizedPuzzleKeys)
        ? baseStats.finalizedPuzzleKeys
        : {},
  };
};

function GameStatusProvider({ children }) {
  const { gameData, puzzleStorageKey, puzzleDate } =
    React.useContext(PuzzleDataContext);
  const initialSavedStateRef = React.useRef(null);
  const hasMarkedCompletedRef = React.useRef(false);

  if (initialSavedStateRef.current == null) {
    const loadedState = loadGameStateFromLocalStorage(puzzleStorageKey);
    console.log("checking game state!", {
      loadedState: loadedState,
      gd1: gameData,
      gd2: loadedState?.gameData,
    });
    if (!isGameDataEquivalent({ gd1: gameData, gd2: loadedState?.gameData })) {
      initialSavedStateRef.current = {
        submittedGuesses: [],
        solvedGameData: [],
      };
    } else if (
      !isGuessesFromGame({
        gameData,
        submittedGuesses: loadedState?.submittedGuesses,
      })
    ) {
      initialSavedStateRef.current = {
        submittedGuesses: [],
        solvedGameData: [],
      };
    } else {
      initialSavedStateRef.current = {
        submittedGuesses: Array.isArray(loadedState?.submittedGuesses)
          ? loadedState.submittedGuesses
          : [],
        solvedGameData: Array.isArray(loadedState?.solvedGameData)
          ? loadedState.solvedGameData
          : [],
      };
    }
  }

  const [submittedGuesses, setSubmittedGuesses] = React.useState(
    () => initialSavedStateRef.current.submittedGuesses
  );
  const [solvedGameData, setSolvedGameData] = React.useState(
    () => initialSavedStateRef.current.solvedGameData
  );
  const [gameStats, setGameStats] = React.useState(() => {
    const stats = loadStatsFromLocalStorage();
    return normalizeStats(stats);
  });

  const [isGameOver, setIsGameOver] = React.useState(false);
  const [isGameWon, setIsGameWon] = React.useState(false);
  const [guessCandidate, setGuessCandidate] = React.useState([]);

  const numMistakesUsed = submittedGuesses.length - solvedGameData.length;

  // use effect to check if game is won
  React.useEffect(() => {
    if (solvedGameData.length === gameData.length) {
      setIsGameOver(true);
      setIsGameWon(true);
    }
    const gameState = { submittedGuesses, solvedGameData, gameData };
    saveGameStateToLocalStorage(gameState, puzzleStorageKey);
  }, [solvedGameData, submittedGuesses, gameData, puzzleStorageKey]);

  // use effect to check if all mistakes have been used and end the game accordingly
  React.useEffect(() => {
    if (numMistakesUsed >= MAX_MISTAKES) {
      setIsGameOver(true);
      setIsGameWon(false);
    }
    const gameState = { submittedGuesses, solvedGameData, gameData };
    saveGameStateToLocalStorage(gameState, puzzleStorageKey);
  }, [
    submittedGuesses,
    numMistakesUsed,
    solvedGameData,
    gameData,
    puzzleStorageKey,
  ]);

  React.useEffect(() => {
    if (!isTodayPuzzleDate(puzzleDate)) {
      return;
    }

    setGameStats((prevStats) => {
      const baseStats = normalizeStats(prevStats);

      if (baseStats.playedPuzzleKeys[puzzleStorageKey]) {
        return baseStats;
      }

      const nextStats = {
        ...baseStats,
        played: baseStats.played + 1,
        lastPlayedDate: puzzleDate,
        playedPuzzleKeys: {
          ...baseStats.playedPuzzleKeys,
          [puzzleStorageKey]: true,
        },
      };

      if (baseStats.lastPlayedDate === puzzleDate) {
        nextStats.currentStreak = baseStats.currentStreak;
      } else {
        let isConsecutiveDay = false;
        if (baseStats.lastPlayedDate) {
          try {
            const prevDate = parseISO(baseStats.lastPlayedDate);
            const currentDate = parseISO(puzzleDate);
            isConsecutiveDay =
              differenceInCalendarDays(currentDate, prevDate) === 1;
          } catch (error) {
            isConsecutiveDay = false;
          }
        }
        nextStats.currentStreak = isConsecutiveDay
          ? baseStats.currentStreak + 1
          : 1;
      }

      nextStats.maxStreak = Math.max(
        baseStats.maxStreak ?? 0,
        nextStats.currentStreak
      );

      saveStatsToLocalStorage(nextStats);
      return nextStats;
    });
  }, [puzzleDate, puzzleStorageKey]);

  React.useEffect(() => {
    if (!isTodayPuzzleDate(puzzleDate) || !isGameOver) {
      return;
    }

    setGameStats((prevStats) => {
      const baseStats = normalizeStats(prevStats);

      if (baseStats.finalizedPuzzleKeys[puzzleStorageKey]) {
        return baseStats;
      }

      const mistakesForPuzzle = Math.min(
        Math.max(numMistakesUsed, 0),
        MAX_MISTAKES
      );

      const nextStats = {
        ...baseStats,
        wins: baseStats.wins + (isGameWon ? 1 : 0),
        totalMistakes: baseStats.totalMistakes + mistakesForPuzzle,
        finalizedPuzzleKeys: {
          ...baseStats.finalizedPuzzleKeys,
          [puzzleStorageKey]: true,
        },
      };

      saveStatsToLocalStorage(nextStats);
      return nextStats;
    });
  }, [
    isGameOver,
    isGameWon,
    numMistakesUsed,
    puzzleDate,
    puzzleStorageKey,
  ]);

  React.useEffect(() => {
    if (!isGameOver || !isGameWon || hasMarkedCompletedRef.current) {
      return;
    }

    hasMarkedCompletedRef.current = true;
    markPuzzleDayCompleted(puzzleDate);
  }, [isGameOver, isGameWon, puzzleDate]);

  return (
    <GameStatusContext.Provider
      value={{
        isGameOver,
        isGameWon,
        numMistakesUsed,
        solvedGameData,
        setSolvedGameData,
        submittedGuesses,
        setSubmittedGuesses,
        guessCandidate,
        setGuessCandidate,
        gameStats,
      }}
    >
      {children}
    </GameStatusContext.Provider>
  );
}

export default GameStatusProvider;
